import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Grid,
  Paper,
} from '@mui/material';
import {
  Print as PrintIcon,
  Edit as EditIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { BonLivraison, BonLivraisonStatus } from '../../types/bonLivraison';

interface BonLivraisonPreviewProps {
  bonLivraison: BonLivraison;
  open: boolean;
  onClose: () => void;
  onPrint: () => void;
  onEdit: () => void;
}

const BonLivraisonPreview: React.FC<BonLivraisonPreviewProps> = ({
  bonLivraison,
  open,
  onClose,
  onPrint,
  onEdit,
}) => {
  const getStatusChip = (status: string) => {
    switch (status) {
      case BonLivraisonStatus.EN_COURS:
        return <Chip label="En cours" color="warning" />;
      case BonLivraisonStatus.LIVRE:
        return <Chip label="Livré" color="success" />;
      case BonLivraisonStatus.ANNULE:
        return <Chip label="Annulé" color="error" />;
      default:
        return <Chip label={status} />;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Aperçu du Bon de Livraison - {bonLivraison.reference}
          </Typography>
          {getStatusChip(bonLivraison.status)}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Header Info */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Informations du Bon de Livraison
              </Typography>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Date:</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {new Date(bonLivraison.date).toLocaleDateString('fr-FR')}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Référence:</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {bonLivraison.reference}
                </Typography>
              </Box>
              {bonLivraison.bon_commande_number && (
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">N° Bon de Commande:</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {bonLivraison.bon_commande_number}
                  </Typography>
                </Box>
              )}
              {bonLivraison.user_name && (
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Créé par:</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {bonLivraison.user_name}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Client
              </Typography>
              <Typography variant="body1" fontWeight={500} gutterBottom>
                {bonLivraison.client_name}
              </Typography>
              {bonLivraison.client_ice && (
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">ICE:</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {bonLivraison.client_ice}
                  </Typography>
                </Box>
              )}
              {bonLivraison.client_address && (
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2">Adresse:</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ textAlign: 'right', maxWidth: '60%' }}>
                    {bonLivraison.client_address}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Line Items */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Articles
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell>Désignation</TableCell>
                    <TableCell align="right">Quantité</TableCell>
                    <TableCell>Commentaire</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bonLivraison.items?.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.designation}</TableCell>
                      <TableCell align="right">
                        {Number(item.quantite).toLocaleString('fr-FR')}
                      </TableCell>
                      <TableCell>{item.commentaire || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {/* Transport Info */}
          {(bonLivraison.transporteur_nom || bonLivraison.transporteur_matricule || bonLivraison.transporteur_cin) && (
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Informations Transport
                </Typography>
                {bonLivraison.transporteur_nom && (
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Transporteur:</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {bonLivraison.transporteur_nom}
                    </Typography>
                  </Box>
                )}
                {bonLivraison.transporteur_matricule && (
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Matricule:</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {bonLivraison.transporteur_matricule}
                    </Typography>
                  </Box>
                )}
                {bonLivraison.transporteur_cin && (
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">CIN:</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {bonLivraison.transporteur_cin}
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          )}

          {/* Conditions/Remarques */}
          {bonLivraison.condition_remarque && (
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Remarques
                </Typography>
                <Typography variant="body2">
                  {bonLivraison.condition_remarque}
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} startIcon={<CloseIcon />}>
          Fermer
        </Button>
        <Button
          onClick={onPrint}
          startIcon={<PrintIcon />}
          variant="outlined"
          sx={{ borderColor: '#4CAF50', color: '#4CAF50' }}
        >
          Imprimer
        </Button>
        <Button
          onClick={onEdit}
          startIcon={<EditIcon />}
          variant="contained"
          sx={{ backgroundColor: '#4CAF50', '&:hover': { backgroundColor: '#388E3C' } }}
        >
          Modifier
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BonLivraisonPreview;
